// v2 ptarmigan — a rotund winter grouse: feathered snowshoe feet under a plump
// puffed-chest body, subtle pale-grey folded wing slabs, the black tail of the
// willow ptarmigan, a round head with a stubby dark beak and the vivid red
// eye-combs arched over each eye. Two plumages: v0 full winter white, v1 the
// spring moult with a rusty head and neck. Ported 1:1 from age-of-mcp's
// v2/ptarmigan.ts, using the library's shared `wetland` (eyes) helper.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const PLUMAGES = [
  { body: "#f4f6f8", chest: "#fbfcfd", wing: "#e2e8ec", head: "#ffffff",
    neck: null as string | null },
  { body: "#eef0f1", chest: "#f6f7f8", wing: "#dde2e6", head: "#8a5a34",
    neck: "#96643c" },
];

function ptarmiganParts(v: number): Part[] {
  const c = PLUMAGES[v % PLUMAGES.length]!;
  const parts: Part[] = [
    // feathered legs and broad snowshoe feet
    { geometry: new THREE.CylinderGeometry(0.0055, 0.005, 0.018, 4), color: c.body,
      position: [0.004, 0.009, 0.011] },
    { geometry: new THREE.CylinderGeometry(0.0055, 0.005, 0.018, 4), color: c.body,
      position: [0.004, 0.009, -0.011] },
    { geometry: new THREE.BoxGeometry(0.016, 0.004, 0.008), color: c.wing,
      position: [0.008, 0.002, 0.011] },
    { geometry: new THREE.BoxGeometry(0.016, 0.004, 0.008), color: c.wing,
      position: [0.008, 0.002, -0.011] },
    // rotund body with a puffed chest
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: c.body,
      position: [0, 0.036, 0], scale: [1.35, 1.05, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.022, 0), color: c.chest,
      position: [0.025, 0.032, 0], scale: [1.0, 1.0, 0.95] },
    // folded wing slabs
    { geometry: new THREE.IcosahedronGeometry(0.019, 0), color: c.wing,
      position: [-0.012, 0.042, 0.02], scale: [1.4, 0.6, 0.45] },
    { geometry: new THREE.IcosahedronGeometry(0.019, 0), color: c.wing,
      position: [-0.012, 0.042, -0.02], scale: [1.4, 0.6, 0.45] },
    // the black willow-ptarmigan tail, slightly raised
    { geometry: new THREE.ConeGeometry(0.013, 0.028, 4), color: "#1e1c1a",
      position: [-0.045, 0.04, 0], rotation: [0, 0, Math.PI / 2 - 0.35],
      scale: [1, 1, 0.45] },
    // round head with a stubby beak
    { geometry: new THREE.IcosahedronGeometry(0.0155, 0), color: c.head,
      position: [0.034, 0.068, 0] },
    { geometry: new THREE.ConeGeometry(0.0045, 0.011, 4), color: "#2a2624",
      position: [0.05, 0.064, 0], rotation: [0, 0, -Math.PI / 2] },
    // vivid red eye-combs arched over the eyes
    { geometry: new THREE.BoxGeometry(0.012, 0.0045, 0.0035), color: "#e03028",
      position: [0.034, 0.0785, 0.0105], rotation: [0, 0, 0.1] },
    { geometry: new THREE.BoxGeometry(0.012, 0.0045, 0.0035), color: "#e03028",
      position: [0.034, 0.0785, -0.0105], rotation: [0, 0, 0.1] },
    ...wetland.eyes(0.042, 0.072, 0.011, 0.0028),
  ];
  if (c.neck) {
    // the spring moult's rusty neck running down to the chest
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.013, 0), color: c.neck,
      position: [0.028, 0.054, 0], scale: [1.0, 1.1, 0.9] });
  }
  return parts;
}

export const ptarmiganV2 = defineModel({
  id: "ptarmigan-v2",
  batchId: "fable",
  name: "Ptarmigan (v2)",
  tags: ["creature","ptarmigan","low-poly","age-of-mcp","fable"],
  summary: "Low-poly ptarmigan.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.06,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => ptarmiganParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => ptarmiganParts(1) },
  ],
  animations: [],
});
